<template>
  <transition name="modal-fade">
    <div v-if="visible" class="feedback-modal-container">
      <div class="feedback-overlay" @click="close" aria-label="关闭投稿对话框"></div>
      <div class="feedback-modal" role="dialog" aria-modal="true" aria-label="条目投稿" @keydown.esc="close">
        <div class="modal-header">
          <h3 class="modal-title"><i class="fa-solid fa-upload"></i> 条目投稿</h3>
          <button class="close-btn" @click="close" aria-label="关闭">
            <i class="fa-solid fa-xmark"></i>
          </button>
        </div>

        <div class="form-row">
          <label class="form-label">名称<span class="req">*</span></label>
          <input class="form-input" v-model="submissionName" placeholder="请输入名称" maxlength="60" />
        </div>

        <div class="form-row">
          <label class="form-label">官网/获取地址<span class="req">*</span></label>
          <input class="form-input" v-model="submissionLink" placeholder="请输入官网或获取地址" maxlength="200" />
          <div class="field-hint" v-if="submissionLink && !isValidLink">链接需以 http 或 https 开头</div>
        </div>

        <div class="form-row">
          <label class="form-label">简介<span class="req">*</span></label>
          <textarea
            v-model="submissionDesc"
            class="feedback-textarea"
            :maxlength="submissionMaxLen"
            placeholder="简要介绍功能与特色"
            rows="6"
          ></textarea>
          <div class="char-counter" :class="{ warn: descTooShort || descTooLong }">字数：{{ submissionCharCount }}/{{ submissionMaxLen }}</div>
          <div class="field-hint" v-if="descTooShort">简介至少 5 字</div>
          <div class="field-hint" v-if="descTooLong">简介最多 {{ submissionMaxLen }} 字</div>
        </div>

        <ul class="tips-list" aria-label="投稿说明">
          <li>链接需以 http 或 https 开头</li>
          <li>简介需 5-{{ submissionMaxLen }} 字</li>
          <li>为保证审核效率，24小时内仅可投稿一次</li>
        </ul>

        <div class="modal-actions">
          <button class="cancel-btn" @click="close">取消</button>
          <button class="clear-btn" :disabled="isSubmissionSubmitting" @click="clearSubmission">清空</button>
          <button class="confirm-btn" :disabled="!canSubmitSubmission || isSubmissionSubmitting" @click="submitSoftwareSubmission">
            <i class="fa-solid fa-paper-plane"></i> {{ isSubmissionSubmitting ? '提交中...' : '提交投稿' }}
          </button>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import axios from 'axios'
import { message, loading } from '@/utils/ui-service'
import serverUrl from '@/assets/BaseServerUrl'
import authService from '@/services/authService'
import eventBus from '@/utils/eventBus'

export default {
  name: 'SoftwareSubmissionModal',
  props: {
    visible: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      submissionName: '',
      submissionLink: '',
      submissionDesc: '',
      submissionMaxLen: 500,
      isSubmissionSubmitting: false
    }
  },
  computed: {
    submissionCharCount() {
      return (this.submissionDesc || '').trim().length
    },
    isValidLink() {
      const link = (this.submissionLink || '').trim()
      return /^https?:\/\//i.test(link)
    },
    descTooShort() {
      return this.submissionCharCount > 0 && this.submissionCharCount < 5
    },
    descTooLong() {
      return this.submissionCharCount > this.submissionMaxLen
    },
    canSubmitSubmission() {
      const okName = !!(this.submissionName && this.submissionName.trim())
      const okLink = this.isValidLink
      const okDesc = !this.descTooShort && !this.descTooLong && !!(this.submissionDesc && this.submissionDesc.trim())
      return okName && okLink && okDesc
    }
  },
  methods: {
    close() {
      this.$emit('update:visible', false)
      this.$emit('close')
    },
    clearSubmission() {
      this.submissionName = ''
      this.submissionLink = ''
      this.submissionDesc = ''
    },
    async submitSoftwareSubmission() {
      if (this.isSubmissionSubmitting) return
      if (!this.canSubmitSubmission) {
        message.warning('请完整填写信息')
        return
      }
      if (!authService.isLoggedIn()) {
        message.warning('请先登录后再进行投稿')
        eventBus.emit('show-login')
        return
      }

      const user = authService.getCurrentUser()
      const token = localStorage.getItem('userToken')
      const form = new FormData()
      form.append('userId', user?.userId)
      form.append('username', user?.username || '匿名用户')
      form.append('name', this.submissionName.trim())
      form.append('link', this.submissionLink.trim())
      form.append('desc', this.submissionDesc.trim())

      this.isSubmissionSubmitting = true
      
      const loadingInstance = loading.service({
        lock: true,
        text: '正在提交并进行AI内容审核，请稍候...',
        background: 'rgba(0, 0, 0, 0.7)',
        customClass: 'ai-loading-overlay'
      })
      
      try {
        const response = await axios.post(`${serverUrl}/api/software-submission`, form, {
          headers: {
            Authorization: token ? `Bearer ${token}` : '',
            'Content-Type': 'multipart/form-data'
          }
        })
        const data = response?.data || {}
        if (data.success) {
          if (data.emailSent === false) {
            message.warning(data.message || '投稿已保存，但邮件发送失败')
          } else {
            message.success(data.message || '投稿成功，感谢你的分享！')
          }
          this.clearSubmission()
          this.close()
        } else {
          const errMsg = data.error || data.message || '投稿失败，请稍后重试'
          throw new Error(errMsg)
        }
      } catch (error) {
        console.error('提交条目投稿失败:', error)
        const status = error?.response?.status
        const serverMsg = error?.response?.data?.error || error?.response?.data?.message
        if (status === 401) {
          message.error(serverMsg || '登录状态异常或令牌过期，请重新登录')
          eventBus.emit('show-login')
        } else if (status === 429) {
          message.warning(serverMsg || '24小时内仅可投稿一次')
        } else {
          message.error(serverMsg || error.message || '投稿失败，请稍后重试')
        }
      } finally {
        this.isSubmissionSubmitting = false
        loadingInstance.close()
      }
    }
  }
}
</script>

<style scoped>
.feedback-modal-container { position: fixed; inset: 0; z-index: var(--z-modal-elevated); }
.feedback-overlay { position: absolute; inset: 0; background: rgba(0,0,0,0.45); }
.feedback-modal {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 92%;
  max-width: 620px;
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 12px 34px rgba(0,0,0,0.24);
  padding: 14px 16px;

}

.dark-theme .feedback-modal { background: #1e1e1e; color: #eaeaea; }
.modal-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 8px; }
.modal-title { margin: 0; font-size: 1.2em; font-family: '爱点风雅黑'; }
.close-btn { background: transparent; border: none; font-size: 1.2em; cursor: pointer; color: #888; }
.dark-theme .close-btn { color: #ccc; }

.form-row { display: flex; flex-direction: column; gap: 6px; margin: 10px 0; }
.form-label { font-size: 0.95em; color: #444; }
.req { color: #dc2626; margin-left: 4px; }
.dark-theme .form-label { color: #cfcfcf; }

.form-input {
  width: calc(100% - 20px);
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 10px;
  font-size: 0.95em;
  background: #fff;
  color: #333;
}
.form-input:focus, .feedback-textarea:focus {
  outline: none;
  border-color: #1a73e8;
  box-shadow: 0 0 0 3px rgba(26,115,232,0.15);
}
.dark-theme .form-input {
  background: #2a2a2a;
  border-color: #444;
  color: #eaeaea;
}
.dark-theme .form-input:focus, .dark-theme .feedback-textarea:focus {
  border-color: #7aa2f7;
  box-shadow: 0 0 0 3px rgba(122,162,247,0.18);
}

.feedback-textarea {
  width: calc(100% - 20px);
  resize: vertical;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 10px;
  font-size: 0.95em;
  min-height: 140px;
  background: white;
  color: #333;
}
.feedback-textarea::placeholder { font-family: '猫啃网糖圆体'; }
.dark-theme .feedback-textarea { background: #2a2a2a; border-color: #444; color: #eaeaea; }

.char-counter { text-align: right; font-size: 0.9em; color: #666; }
.char-counter.warn { color: #b91c1c; }
.dark-theme .char-counter { color: #c8c8c8; }

.tips-list { margin: 10px 0 0; padding-left: 18px; color: #666; font-size: 0.92em; }
.dark-theme .tips-list { color: #c8c8c8; }

.field-hint { font-size: 12px; color: #b91c1c; margin-top: 4px; }
.dark-theme .field-hint { color: #fca5a5; }

.modal-actions { display: flex; justify-content: flex-end; gap: 8px; margin-top: 12px; }
.cancel-btn, .confirm-btn, .clear-btn { padding: 8px 14px; border-radius: 10px; border: none; cursor: pointer; transition: all 0.3s; }
.cancel-btn { background: #f3f3f3; color: #333; }
.clear-btn { background: #fee2e2; color: #b91c1c; }
.confirm-btn { background: #1a73e8; color: #fff; }
.confirm-btn:disabled { opacity: 0.6; cursor: not-allowed; }

.modal-fade-enter-active, .modal-fade-leave-active { transition: opacity 0.2s ease; }
.modal-fade-enter-from, .modal-fade-leave-to { opacity: 0; }

@media (max-width: 480px) {
  .feedback-modal { 
      inset: 0; 
      left: 0; 
      top: 0; 
      transform: none; 
      width: calc(100% - 32px); 
      max-width: none; 
      height: calc(100% - 32px); 
      max-height: none; 
      border-radius: 0; 
      padding: 16px;
      overflow-y: auto; 
      display: flex; 
      flex-direction: column; 
  }
  .modal-actions { 
      margin-top: auto; 
  }
}
</style>
